// ─── Knowledge Capture Pipeline — Client-Side Extractors ─────────────────────
// Extraction that can run in the browser stays in the browser: PDFs (pdf.js),
// Word docs (mammoth), spreadsheets (SheetJS), EPUBs (jszip), plain text,
// and image thumbnailing. Heavy libs load via dynamic import so they never
// weigh down the initial bundle.

export type FileKind =
  | 'pdf'
  | 'docx'
  | 'sheet'
  | 'epub'
  | 'text'
  | 'image'
  | 'audio'
  | 'unsupported';

export const MAX_FILE_BYTES = 50 * 1024 * 1024;
const MAX_PDF_PAGES = 400;
const MAX_TEXT_CHARS = 500_000;

export interface ExtractedFile {
  text: string;
  pageCount: number | null;
  /** Small JPEG data URL rendered from the document/image, if possible. */
  thumbnailDataUrl: string | null;
  /** Title found inside the document metadata, if any. */
  docTitle: string | null;
}

export function detectFileKind(file: File): FileKind {
  const name = file.name.toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop()! : '';
  const mime = (file.type || '').toLowerCase();

  if (ext === 'pdf' || mime === 'application/pdf') return 'pdf';
  if (ext === 'docx' || mime.includes('officedocument.wordprocessingml')) return 'docx';
  if (['xlsx', 'xls', 'csv', 'ods'].includes(ext) || mime.includes('spreadsheet') || mime === 'text/csv') return 'sheet';
  if (ext === 'epub' || mime === 'application/epub+zip') return 'epub';
  if (['txt', 'md', 'markdown', 'text', 'log', 'json'].includes(ext) || mime.startsWith('text/')) return 'text';
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'webp', 'gif', 'heic'].includes(ext)) return 'image';
  if (mime.startsWith('audio/') || ['mp3', 'm4a', 'wav', 'ogg', 'webm', 'flac'].includes(ext)) return 'audio';
  return 'unsupported';
}

export function fileExtension(file: File): string | null {
  const name = file.name.toLowerCase();
  return name.includes('.') ? name.split('.').pop()! : null;
}

// ─── PDF ──────────────────────────────────────────────────────────────────────

export async function extractPdf(
  file: File,
  onProgress?: (detail: string) => void,
): Promise<ExtractedFile> {
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
  ).toString();

  const data = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data }).promise;
  const pageCount = doc.numPages;
  const pagesToRead = Math.min(pageCount, MAX_PDF_PAGES);

  const parts: string[] = [];
  for (let p = 1; p <= pagesToRead; p++) {
    if (p % 5 === 1) onProgress?.(`Reading page ${p} of ${pagesToRead}`);
    const page = await doc.getPage(p);
    const content = await page.getTextContent();
    const pageText = content.items
      .map(item => ('str' in item ? item.str : ''))
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (pageText) parts.push(pageText);
    if (parts.join('\n').length > MAX_TEXT_CHARS) break;
  }

  // Thumbnail from page 1.
  let thumbnailDataUrl: string | null = null;
  try {
    const page1 = await doc.getPage(1);
    const viewport = page1.getViewport({ scale: 1 });
    const scale = 220 / viewport.width;
    const scaled = page1.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(scaled.width);
    canvas.height = Math.ceil(scaled.height);
    const ctx = canvas.getContext('2d');
    if (ctx) {
      await page1.render({ canvasContext: ctx, viewport: scaled }).promise;
      thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.75);
    }
  } catch { /* thumbnail is optional */ }

  let docTitle: string | null = null;
  try {
    const meta = await doc.getMetadata();
    const info = meta.info as { Title?: string } | undefined;
    if (info?.Title && info.Title.trim()) docTitle = info.Title.trim();
  } catch { /* optional */ }

  await doc.destroy();

  return {
    text: parts.join('\n\n').slice(0, MAX_TEXT_CHARS),
    pageCount,
    thumbnailDataUrl,
    docTitle,
  };
}

// ─── DOCX ─────────────────────────────────────────────────────────────────────

export async function extractDocx(file: File): Promise<ExtractedFile> {
  const mammoth = await import('mammoth');
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return {
    text: (result.value ?? '').trim().slice(0, MAX_TEXT_CHARS),
    pageCount: null,
    thumbnailDataUrl: null,
    docTitle: null,
  };
}

// ─── Spreadsheets / CSV ───────────────────────────────────────────────────────

export async function extractSheet(file: File): Promise<ExtractedFile> {
  const XLSX = await import('xlsx');
  const data = await file.arrayBuffer();
  const wb = XLSX.read(data, { type: 'array' });
  const parts: string[] = [];
  for (const sheetName of wb.SheetNames.slice(0, 10)) {
    const csv = XLSX.utils.sheet_to_csv(wb.Sheets[sheetName]);
    if (csv.trim()) {
      parts.push(`## Sheet: ${sheetName}\n${csv.trim()}`);
    }
    if (parts.join('\n').length > MAX_TEXT_CHARS) break;
  }
  return {
    text: parts.join('\n\n').slice(0, MAX_TEXT_CHARS),
    pageCount: wb.SheetNames.length,
    thumbnailDataUrl: null,
    docTitle: null,
  };
}

// ─── EPUB ─────────────────────────────────────────────────────────────────────

export async function extractEpub(
  file: File,
  onProgress?: (detail: string) => void,
): Promise<ExtractedFile> {
  const JSZip = (await import('jszip')).default;
  const zip = await JSZip.loadAsync(await file.arrayBuffer());

  const containerXml = await zip.file('META-INF/container.xml')?.async('string');
  if (!containerXml) throw new Error('Not a valid EPUB (missing container.xml).');

  const parser = new DOMParser();
  const containerDoc = parser.parseFromString(containerXml, 'application/xml');
  const rootfilePath = containerDoc
    .querySelector('rootfile')
    ?.getAttribute('full-path');
  if (!rootfilePath) throw new Error('Not a valid EPUB (no rootfile).');

  const opfXml = await zip.file(rootfilePath)?.async('string');
  if (!opfXml) throw new Error('Not a valid EPUB (missing package file).');
  const opfDoc = parser.parseFromString(opfXml, 'application/xml');
  const opfDir = rootfilePath.includes('/')
    ? rootfilePath.slice(0, rootfilePath.lastIndexOf('/') + 1)
    : '';

  const docTitle = opfDoc.querySelector('title')?.textContent?.trim() ?? null;

  const manifest = new Map<string, string>();
  opfDoc.querySelectorAll('manifest > item').forEach(item => {
    const id = item.getAttribute('id');
    const href = item.getAttribute('href');
    if (id && href) manifest.set(id, href);
  });

  const spineIds: string[] = [];
  opfDoc.querySelectorAll('spine > itemref').forEach(ref => {
    const idref = ref.getAttribute('idref');
    if (idref) spineIds.push(idref);
  });

  const parts: string[] = [];
  let read = 0;
  for (const id of spineIds) {
    const href = manifest.get(id);
    if (!href) continue;
    const path = decodeURIComponent(opfDir + href);
    const html = await zip.file(path)?.async('string');
    if (!html) continue;
    read++;
    if (read % 5 === 1) onProgress?.(`Reading chapter ${read} of ${spineIds.length}`);
    const doc = parser.parseFromString(html, 'text/html');
    const text = (doc.body?.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (text) parts.push(text);
    if (parts.join('\n').length > MAX_TEXT_CHARS) break;
  }

  return {
    text: parts.join('\n\n').slice(0, MAX_TEXT_CHARS),
    pageCount: spineIds.length,
    thumbnailDataUrl: null,
    docTitle,
  };
}

// ─── Plain text ───────────────────────────────────────────────────────────────

export async function extractPlainText(file: File): Promise<ExtractedFile> {
  const text = await file.text();
  return {
    text: text.slice(0, MAX_TEXT_CHARS),
    pageCount: null,
    thumbnailDataUrl: null,
    docTitle: null,
  };
}

// ─── Images ───────────────────────────────────────────────────────────────────

/** Downscale an image file/blob to a small JPEG data URL. */
export function makeImageThumbnail(blob: Blob, maxEdge = 320): Promise<string | null> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      } catch {
        resolve(null);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

/** Full-size (bounded) data URL for sending to the vision endpoint. */
export function imageToDataUrl(blob: Blob, maxEdge = 1600): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas unavailable')); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.86));
      } catch (err) {
        reject(err instanceof Error ? err : new Error('Image processing failed'));
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read this image file.')); };
    img.src = url;
  });
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read file.'));
    reader.readAsDataURL(blob);
  });
}

// ─── Hashing (duplicate detection) ────────────────────────────────────────────

export async function sha256Hex(input: string | ArrayBuffer): Promise<string> {
  try {
    const data = typeof input === 'string' ? new TextEncoder().encode(input) : new Uint8Array(input);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    // Insecure-context fallback: djb2 (weaker but deterministic).
    const str = typeof input === 'string'
      ? input
      : String.fromCharCode(...new Uint8Array(input.slice(0, 65536)));
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
    return `djb2_${h.toString(16)}_${str.length}`;
  }
}
